
import { useMemo, useState } from 'react';
import LeftArrowIcon from './arrow-left.svg';
import RightArrowIcon from './arrow-right.svg';
import styles from './Pagination.module.scss';

interface PaginationProps {
  /**
   * 총 페이지 수
   */
  totalPage: number;
  /**
   * 현재 페이지
   */
  currentPage: number;

  /**
   * 페이지 크기 (기본값: false)
   * true로 설정하면 보여지는 페이지의 개수가 5개에서 7개로 늘어납니다.
   */
  large?: boolean;
  /**
   * 페이지 클릭 핸들러
   */
  onPageClick: (page: number) => void;
}

export default function Pagination({ totalPage, currentPage, onPageClick, large = false }: PaginationProps) {
  const [animation, setAnimation] = useState<'left' | 'right' | 'doubleRight' | 'doubleLeft' | 'tripleRight' | 'tripleLeft' | ''>('');
  const [selectedPage, setSelectedPage] = useState(currentPage || 1);
  const weight = useMemo(() => (large ? 2 : 0), [large]);

  const pageArray = useMemo(() => {
    if (!totalPage) return Array.from({ length: 9 + weight }).map((_, i) => (i === 2 ? 1 : null));

    return Array.from({ length: 9 + weight }).map((_, i) => {
      const pageIndex = selectedPage + i - 2 - (weight / 2) ;

      if (pageIndex < 1 || pageIndex > totalPage) {
        return null;
      }
      return pageIndex;
    });
  }, [selectedPage, totalPage, weight]);

  const handlePageClick = (page: number) => {
    if (page > totalPage || page < 1) return;
    onPageClick(page);
    if (page === null) return;
    const diff = page - selectedPage;

    switch (diff) {
      case 1:
        setAnimation('right');
        break;
      case -1:
        setAnimation('left');
        break;
      case 2:
        setAnimation('doubleRight');
        break;
      case -2:
        setAnimation('doubleLeft');
        break;
      case 3:
        setAnimation('tripleRight');
        break;
      case -3:
        setAnimation('tripleLeft');
        break;
      default:
        setAnimation('');
    }

    setSelectedPage(page);

    setTimeout(() => {
      setAnimation('');
    }, 200);
  };

  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.arrow} ${selectedPage <= 1 ? styles['arrow--disabled'] : '' }`}
        type="button"
        onClick={() => handlePageClick(selectedPage - 1)}
      >
        <LeftArrowIcon />
      </button>
      <div
        className={styles.slider}
        style={{ width: `${(8 + weight) * 40}px` }}
      >
        <div
          className={styles.track}
        />
        <div className={styles.portal}>
          {selectedPage > (3 + weight / 2) && (
            < >
              <button
                className={styles.page}
                type="button"
                onClick={() => handlePageClick(1)}
              >1
              </button>
              <span className={styles.portal__ellipsis}>•••</span>
            </>
          )}
        </div>
        <div
          className={styles.pages}
          style={{ width: `${(5 + weight) * 40}px` }}
        >
          <div className={`${styles.pageWrapper} ${animation === 'left' ? styles.left : ''} ${styles[animation]}`}>
            {pageArray.map((page, index) => (
              <button
                className={`${styles.page} ${selectedPage === page ? styles['page--selected'] : ''}`}
                key={index}
                type="button"
                disabled={page === null}
                onClick={() => {
                  if (page !== null) {
                    handlePageClick(page);
                  }
                }}
              >
                <span>{page}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={styles.portal}>
          {selectedPage < totalPage - (2 + weight / 2) && (
            <>
              <span className={styles.portal__ellipsis}>•••</span>
              <button
                className={styles.page}
                type="button"
                onClick={() => handlePageClick(totalPage)}
              >{totalPage}
              </button>
            </>
          )}
        </div>
      </div>
      <button
        className={`${styles.arrow} ${selectedPage >= totalPage ? styles['arrow--disabled'] : ''}`}
        type="button"
        onClick={() => handlePageClick(selectedPage + 1)}
      >
        <RightArrowIcon />
      </button>
    </div>
  );
}
